import { useState } from 'react';
import { Color, PieceType } from '../types';
import { validateBlindBackRank } from '../engine/chessEngine';
import { chess960BackRankFromId, chess960PositionIdFromRank, randomChess960BackRank } from '../utils/chess960Utils';
import { PieceGlyph } from './PieceGlyph';
import { Badge } from './ui/Badge';
import { Button } from './ui/Button';
import { Card } from './ui/Card';

interface BlindSetupScreenProps {
  /** The player's current 8-character back-rank arrangement. */
  backRank: string;
  onBackRankChange: (rank: string) => void;
  /** The shared major piece occupying the queen slot (public for both players). */
  sharedMajorPiece: PieceType;
  ownColor: Color;
  submitted: boolean;
  opponentSubmitted: boolean;
  /** null → the opponent has not joined the room yet (private room). */
  opponentName: string | null;
  ownName: string;
  onSubmit: () => void;
  onLeaveRoom: () => void;
  onCopyInviteLink?: () => void;
}

const FILES = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];

const PIECE_LABELS: Record<PieceType, string> = {
  K: 'King',
  Q: 'Queen',
  R: 'Rook',
  B: 'Bishop',
  N: 'Knight',
  P: 'Pawn',
  H: 'Hawk',
  E: 'Elephant',
  A: 'Amazon'
};

const ruleChecks = (rank: string): { bishops: boolean; kingBetweenRooks: boolean } => {
  const pieces = rank.split('') as PieceType[];
  const bishopCols: number[] = [];
  const rookCols: number[] = [];
  let kingCol = -1;
  pieces.forEach((piece, col) => {
    if (piece === 'B') bishopCols.push(col);
    if (piece === 'R') rookCols.push(col);
    if (piece === 'K') kingCol = col;
  });

  return {
    bishops: bishopCols.length === 2 && (bishopCols[0] % 2) !== (bishopCols[1] % 2),
    kingBetweenRooks: rookCols.length === 2 && kingCol > rookCols[0] && kingCol < rookCols[1]
  };
};

const pieceWrapperClass = (color: Color): string => (
  color === 'white' ? 'chess-piece chess-piece--white' : 'chess-piece chess-piece--black'
);

export const BlindSetupScreen: React.FC<BlindSetupScreenProps> = ({
  backRank,
  onBackRankChange,
  sharedMajorPiece,
  ownColor,
  submitted,
  opponentSubmitted,
  opponentName,
  ownName,
  onSubmit,
  onLeaveRoom,
  onCopyInviteLink
}) => {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);

  const rankPieces = backRank.split('') as PieceType[];
  const validationError = validateBlindBackRank(backRank, sharedMajorPiece);
  const isValid = validationError === null;
  const checks = ruleChecks(backRank);
  const majorPieceOk = rankPieces.filter((piece) => piece === sharedMajorPiece).length === 1;
  const positionId = isValid ? chess960PositionIdFromRank(backRank) : null;
  const opponentJoined = opponentName !== null;

  const swap = (from: number, to: number) => {
    if (from === to) return;
    const next = [...rankPieces];
    const piece = next[from];
    next[from] = next[to];
    next[to] = piece;
    onBackRankChange(next.join(''));
  };

  const shuffle = () => {
    onBackRankChange(randomChess960BackRank(sharedMajorPiece));
    setSelectedIndex(null);
    setDraggingIndex(null);
  };

  const resetToStandard = () => {
    onBackRankChange(chess960BackRankFromId(518, sharedMajorPiece));
    setSelectedIndex(null);
    setDraggingIndex(null);
  };

  const statusText = (() => {
    if (!submitted) {
      return opponentSubmitted
        ? 'Your opponent is ready — submit your setup to start the game.'
        : 'Arrange the pieces on your first rank, then submit your setup.';
    }
    if (!opponentJoined) {
      return 'Setup saved. Share the invite link — the game starts automatically once your opponent joins and submits.';
    }
    if (!opponentSubmitted) {
      return `Setup saved. Waiting for ${opponentName} to submit… You can still adjust and resubmit.`;
    }
    return 'Both setups are in — revealing the board…';
  })();

  return (
    <div className="w-full max-w-5xl mx-auto space-y-4 animate-in fade-in slide-in-from-bottom-6 duration-500">
      <Card variant="glass" padding="lg">
        <div className="text-center">
          <Badge variant="primary">Blind Chess960</Badge>
          <h2 className="mt-3 text-2xl font-bold text-white">Secret First-Rank Setup</h2>
          <p className="mt-2 text-sm text-slate-300">
            Arrange the pieces on <span className="font-semibold text-cyan-200">your</span> first rank.
            Your opponent cannot see your arrangement — and you cannot see theirs.
            The board is revealed to both players the moment both setups are submitted.
          </p>
          <p className="mt-2 text-xs text-slate-400">
            Shared major piece: <span className="font-semibold text-slate-200">{PIECE_LABELS[sharedMajorPiece]}</span> (same for both players).
          </p>
        </div>
      </Card>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,22rem)]">
        {/* Board preview + interactive rank */}
        <Card variant="glass" padding="md">
          <div className="flex items-center justify-between gap-2 mb-3">
            <h3 className="text-lg font-bold text-white">Your side</h3>
            {positionId !== null && (
              <span className="rounded-md border border-cyan-500/30 bg-cyan-500/20 px-2 py-0.5 font-mono text-xs text-cyan-200">
                Chess960 #{positionId}
              </span>
            )}
          </div>

          <div className="flex justify-center">
            <div className="w-full max-w-md">
              {/* Opponent side (hidden) */}
              <div className="grid grid-cols-8 gap-0.5">
                {Array.from({ length: 16 }, (_, index) => (
                  <div
                    key={index}
                    className="aspect-square flex items-center justify-center rounded-sm text-slate-500 bg-slate-800/90 border border-slate-700/60 select-none"
                    title="Opponent's pieces — hidden until the game starts"
                  >
                    <span className="text-sm font-bold opacity-50">?</span>
                  </div>
                ))}
              </div>
              <div className="my-1.5 text-center text-[11px] uppercase tracking-wider text-slate-500">
                Opponent&apos;s side — hidden until the game starts
              </div>

              {/* Middle ranks (empty) */}
              <div className="grid grid-cols-8 gap-0.5">
                {Array.from({ length: 32 }, (_, index) => (
                  <div
                    key={index}
                    className="aspect-square rounded-sm bg-slate-900/40 border border-slate-800/60"
                  />
                ))}
              </div>

              {/* Own pawns */}
              <div className="grid grid-cols-8 gap-0.5">
                {Array.from({ length: 8 }, (_, col) => {
                  const isLight = ((ownColor === 'white' ? 6 : 1) + col) % 2 === 0;
                  return (
                    <div
                      key={col}
                      className={`chess-square aspect-square flex items-center justify-center rounded-sm ${isLight ? 'chess-square--light' : 'chess-square--dark'}`}
                    >
                      <div className={`${pieceWrapperClass(ownColor)} text-2xl sm:text-3xl select-none`}>
                        <PieceGlyph piece="P" color={ownColor} />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Interactive back rank */}
              <div className="grid grid-cols-8 gap-0.5 mt-0.5">
                {rankPieces.map((piece, index) => {
                  const isSelected = selectedIndex === index;
                  const isDragging = draggingIndex === index;
                  const isLight = (index % 2) === 0;
                  return (
                    <div
                      key={index}
                      draggable={true}
                      onDragStart={(event) => {
                        setDraggingIndex(index);
                        setSelectedIndex(null);
                        event.dataTransfer.effectAllowed = 'move';
                        event.dataTransfer.setData('text/plain', String(index));
                      }}
                      onDragOver={(event) => event.preventDefault()}
                      onDrop={(event) => {
                        event.preventDefault();
                        const from = draggingIndex;
                        if (from !== null) {
                          swap(from, index);
                        }
                        setDraggingIndex(null);
                      }}
                      onDragEnd={() => setDraggingIndex(null)}
                      onClick={() => {
                        if (selectedIndex === null) {
                          setSelectedIndex(index);
                        } else if (selectedIndex === index) {
                          setSelectedIndex(null);
                        } else {
                          swap(selectedIndex, index);
                          setSelectedIndex(null);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          if (selectedIndex === null) {
                            setSelectedIndex(index);
                          } else if (selectedIndex === index) {
                            setSelectedIndex(null);
                          } else {
                            swap(selectedIndex, index);
                            setSelectedIndex(null);
                          }
                        }
                      }}
                      aria-label={`${PIECE_LABELS[piece]} on ${FILES[index]}`}
                      className={`chess-square aspect-square relative flex items-center justify-center rounded-sm cursor-pointer transition-all duration-100 select-none ${
                        isLight ? 'chess-square--light' : 'chess-square--dark'
                      } ${
                        isSelected
                          ? 'ring-inset ring-[3px] ring-orange-400/90 shadow-[inset_0_0_12px_rgba(249,115,22,0.5)] z-10'
                          : isDragging
                            ? 'opacity-40'
                            : 'hover:ring-inset hover:ring-2 hover:ring-cyan-300/70'
                      }`}
                      style={{ touchAction: 'none', userSelect: 'none', WebkitUserSelect: 'none' }}
                    >
                      <div
                        className={`${pieceWrapperClass(ownColor)} text-2xl sm:text-3xl select-none cursor-grab active:cursor-grabbing hover:scale-110 transition-transform duration-150`}
                      >
                        <PieceGlyph piece={piece} color={ownColor} />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* File labels */}
              <div className="grid grid-cols-8 gap-0.5 mt-1">
                {FILES.map((file) => (
                  <div key={file} className="text-center text-[11px] font-mono text-slate-500">
                    {file}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-3 text-center text-xs text-slate-500">
            Tap or drag two squares to swap pieces. Pawns stay on their rank automatically.
          </div>
        </Card>

        {/* Rules + actions */}
        <div className="space-y-4">
          <Card variant="glass" padding="md">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-cyan-200 mb-3">Chess960 rules</h3>
            <ul className="space-y-2 text-sm">
              <li className={`flex items-center gap-2 ${checks.bishops ? 'text-emerald-200' : 'text-rose-200'}`}>
                <span className="w-4 text-center font-bold">{checks.bishops ? '✓' : '✗'}</span>
                Bishops on opposite-colored squares
              </li>
              <li className={`flex items-center gap-2 ${checks.kingBetweenRooks ? 'text-emerald-200' : 'text-rose-200'}`}>
                <span className="w-4 text-center font-bold">{checks.kingBetweenRooks ? '✓' : '✗'}</span>
                King between the two rooks
              </li>
              <li className={`flex items-center gap-2 ${majorPieceOk ? 'text-emerald-200' : 'text-rose-200'}`}>
                <span className="w-4 text-center font-bold">{majorPieceOk ? '✓' : '✗'}</span>
                {PIECE_LABELS[sharedMajorPiece]} in the major-piece slot
              </li>
            </ul>
            {!isValid && validationError && (
              <p className="mt-3 rounded-lg border border-rose-400/30 bg-rose-500/10 p-2.5 text-xs text-rose-200">
                {validationError}
              </p>
            )}
          </Card>

          <Card variant="glass" padding="md">
            <div className="rounded-lg border border-cyan-400/20 bg-cyan-500/10 p-3 text-sm text-cyan-100">
              {statusText}
            </div>

            <div className="mt-4 space-y-2">
              {!submitted && (
                <div className="flex flex-wrap gap-2">
                  <Button variant="secondary" onClick={shuffle}>
                    🎲 Shuffle
                  </Button>
                  <Button variant="secondary" onClick={resetToStandard}>
                    Standard arrangement
                  </Button>
                </div>
              )}
              <Button
                onClick={onSubmit}
                disabled={!isValid}
                size="lg"
                className="w-full"
              >
                {submitted ? 'Update setup ✓' : 'Submit setup'}
              </Button>
              {onCopyInviteLink && !opponentJoined && (
                <Button variant="secondary" onClick={onCopyInviteLink} className="w-full">
                  Copy invite link
                </Button>
              )}
              <Button variant="ghost" onClick={onLeaveRoom} className="w-full">
                Leave room
              </Button>
            </div>

            <div className="mt-4 flex items-center justify-between text-xs text-slate-400">
              <span>Playing as <span className="font-semibold text-slate-200">{ownColor}</span> ({ownName})</span>
              <span>
                {opponentJoined ? (
                  <>Opponent: <span className="font-semibold text-slate-200">{opponentName}</span></>
                ) : (
                  'Opponent: not joined yet'
                )}
              </span>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};
